import { useState } from 'react';
import { Button, Dropdown, Select, Label, ListBox } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslation } from '@rhino-project/core/i18n';
import { getLanguageName as languageName } from '../../utils/language';
import { LanguagePicker } from './LanguagePicker';

export interface LanguageSwitcherProps {
  variant?: 'field' | 'compact' | 'icon';
  className?: string;
}

export const LanguageSwitcher = ({
  variant = 'field',
  className
}: LanguageSwitcherProps) => {
  const {
    pickerLocale: locale,
    pickerLocales: availableLocales,
    setLocale,
    t
  } = useTranslation();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  if (availableLocales.length < 2) return null;

  if (variant === 'icon') {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          isIconOnly
          className={className}
          aria-label={`${t('rhino.frontend.language')}: ${languageName(locale)}`}
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
          onPress={() => setPickerOpen(true)}
        >
          <Icon icon="bi:globe2" aria-hidden="true" />
        </Button>
        <LanguagePicker isOpen={pickerOpen} onOpenChange={setPickerOpen} />
      </>
    );
  }

  const changeLanguage = async (value: string) => {
    setPending(true);
    setFailed(false);
    try {
      await setLocale(value);
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={className}>
      {variant === 'compact' ? (
        <Dropdown>
          <Button
            variant="ghost"
            size="sm"
            isDisabled={pending}
            aria-label={`${t('rhino.frontend.language')}: ${languageName(locale)}`}
          >
            <Icon icon="bi:globe2" aria-hidden="true" />
            {languageName(locale)}
          </Button>
          <Dropdown.Popover>
            <Dropdown.Menu
              aria-label={t('rhino.frontend.language')}
              selectionMode="single"
              selectedKeys={[locale]}
              onAction={(key) => void changeLanguage(String(key))}
            >
              {availableLocales.map((code) => (
                <Dropdown.Item
                  key={code}
                  id={code}
                  textValue={languageName(code)}
                >
                  <Label>{languageName(code)}</Label>
                  <Dropdown.ItemIndicator />
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      ) : (
        <Select
          value={locale}
          isDisabled={pending}
          onChange={(value) => {
            if (typeof value === 'string') void changeLanguage(value);
          }}
        >
          <Label>{t('rhino.frontend.language')}</Label>
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {availableLocales.map((code) => {
                const name = languageName(code);
                return (
                  <ListBox.Item key={code} id={code} textValue={name}>
                    <Label>{name}</Label>
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                );
              })}
            </ListBox>
          </Select.Popover>
        </Select>
      )}
      {failed && (
        <p role="alert">{t('rhino.frontend.messages.language_load_error')}</p>
      )}
    </div>
  );
};
